import React, { useEffect, useState, useCallback } from "react";
import { Alert, Button, Form, InputGroup, ListGroup, Stack, ToggleButton, ToggleButtonGroup } from "react-bootstrap";
import { FaRegLightbulb, FaPlus, FaArrowUp, FaArrowDown, FaArrowLeft, FaArrowRight } from "react-icons/fa6";
import { IoWarningOutline } from "react-icons/io5";
import CreatePlace from "./CreateNewPlace";
import EditPlace from "./EditNewPlace";
import CreateProductCategory from "./CreateNewCategory";
import EditCategory from "./EditNewCategory";
import {
    getNewProducts, getNewPlaces, getNewProductCategories,
    reorderCategories, reorderProducts, exportProductsToExcel
} from "../../apiForNewProducts";
import { FaCircleInfo } from "react-icons/fa6";
import EditNewProduct from "./EditNewProduct";
import CreateNewProduct from "./CreateNewProduct";

function SellNewProducts({ jwt }) {
    const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
    const [places, setPlaces] = useState([]);
    const [currentPlaceId, setCurrentPlaceId] = useState(2);
    const [currentCategoryId, setCurrentCategoryId] = useState(1);
    const [hasAccess, setHasAccess] = useState(true);
    const [loading, setLoading] = useState(true);
    const [fixingPriorities, setFixingPriorities] = useState(false);

    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);

    const [isCreatePlace, setIsCreatePlace] = useState(false);
    const [isCreateCategory, setIsCreateCategory] = useState(false);
    const [isCreateProduct, setIsCreateProduct] = useState(false);

    const [placePoints, setPlacePoints] = useState({});
    const [categoryPoints, setCategoryPoints] = useState({});

    const [isEditingCategoryOrder, setIsEditingCategoryOrder] = useState(false);
    const [isEditingProductOrder, setIsEditingProductOrder] = useState(false);

    const [exporting, setExporting] = useState(false);

    // Функция для проверки наличия дубликатов приоритетов
    const hasDuplicatePriorities = useCallback((productsList) => {
        const categoryGroups = {};
        let hasDuplicates = false;

        // Группируем продукты по категориям
        productsList.forEach(product => {
            const categoryKey = product.categoryId || 'null';
            if (!categoryGroups[categoryKey]) {
                categoryGroups[categoryKey] = [];
            }
            categoryGroups[categoryKey].push(product);
        });

        // Проверяем каждую категорию на дубликаты
        for (const [categoryKey, categoryProducts] of Object.entries(categoryGroups)) {
            const prioritySet = new Set();
            for (const product of categoryProducts) {
                if (prioritySet.has(product.priority)) {
                    hasDuplicates = true;
                    break;
                }
                prioritySet.add(product.priority);
            }
            if (hasDuplicates) break;
        }

        return hasDuplicates;
    }, []);

    // Автоматическое исправление дубликатов приоритетов
    const autoFixDuplicatePriorities = useCallback(async (productsList) => {
        if (!productsList || productsList.length === 0) return productsList;

        // Проверяем, есть ли дубликаты
        if (!hasDuplicatePriorities(productsList)) {
            console.log('Дубликаты приоритетов не найдены');
            return productsList;
        }

        console.log('Обнаружены дубликаты приоритетов. Автоматическое исправление...');
        setFixingPriorities(true);

        try {
            // Группируем продукты по категориям
            const categoryGroups = {};

            productsList.forEach(product => {
                const categoryKey = product.categoryId || 'null';
                if (!categoryGroups[categoryKey]) {
                    categoryGroups[categoryKey] = [];
                }
                categoryGroups[categoryKey].push(product);
            });

            const allUpdateRequests = [];

            // Для каждой категории создаем уникальные приоритеты
            for (const [categoryKey, categoryProducts] of Object.entries(categoryGroups)) {
                // Сортируем по текущему приоритету и ID
                const sortedProducts = [...categoryProducts].sort((a, b) => {
                    if (a.priority === b.priority) {
                        return a.id - b.id;  // При равном приоритете сортируем по ID
                    }
                    return a.priority - b.priority;
                });

                // Присваиваем новые приоритеты (0, 1, 2, ...)
                sortedProducts.forEach((product, index) => {
                    if (product.priority !== index) {
                        allUpdateRequests.push({
                            id: product.id,
                            priority: index
                        });
                    }
                });
            }

            if (allUpdateRequests.length > 0) {
                console.log(`Найдено ${allUpdateRequests.length} элементов для исправления`);

                // Отправляем запросы пачками
                for (let i = 0; i < allUpdateRequests.length; i += 10) {
                    const batch = allUpdateRequests.slice(i, i + 10);
                    await reorderProducts(batch, jwt);
                }

                // Перезагружаем продукты
                const responseProducts = await getNewProducts(jwt);
                console.log('Приоритеты успешно исправлены');
                setFixingPriorities(false);
                return responseProducts ?? [];
            } else {
                console.log('Дубликатов не обнаружено');
                setFixingPriorities(false);
                return productsList;
            }
        } catch (error) {
            console.error('Ошибка при автоматическом исправлении приоритетов:', error);
            setFixingPriorities(false);
            // В случае ошибки возвращаем исходный список
            return productsList;
        }
    }, [jwt, hasDuplicatePriorities]);

    async function didMount() {
        setLoading(true);
        try {
            const [responsePlaces, responseCategories, responseProducts] = await Promise.all([
                getNewPlaces(jwt),
                getNewProductCategories(jwt, true),
                getNewProducts(jwt)
            ]);

            if (responsePlaces && responsePlaces.length === 0) {
                setHasAccess(false);
            } else {
                setHasAccess(true);
                setPlaces(responsePlaces ?? []);
                if (responsePlaces && responsePlaces.length > 0) {
                    setCurrentPlaceId(responsePlaces[0].id);
                }
            }

            setCategories(responseCategories ?? []);

            // Автоматически исправляем приоритеты при загрузке
            let processedProducts = responseProducts ?? [];
            if (processedProducts.length > 0) {
                processedProducts = await autoFixDuplicatePriorities(processedProducts);
            }
            setProducts(processedProducts);

            if (responseCategories && responseCategories.length > 0) {
                const availableCategory = responseCategories.find(category => {
                    if (!category.places || category.places.length === 0) return true;
                    return category.places.some(place => place.newProductPlaceId === currentPlaceId);
                });

                if (availableCategory) {
                    setCurrentCategoryId(availableCategory.id);
                } else if (responseProducts.findIndex(a => a.categoryId == null) !== -1) {
                    setCurrentCategoryId(-2);
                }
            }

        } catch (error) {
            console.error("Ошибка загрузки:", error);
            setHasAccess(false);
        } finally {
            setLoading(false);
        }
    }

    const handleExport = async () => {
        try {
            setExporting(true);
            await exportProductsToExcel(jwt);
            alert('Экспорт завершен успешно!');
        } catch (error) {
            console.error('Ошибка экспорта:', error);
            alert('Ошибка при экспорте: ' + error.message);
        } finally {
            setExporting(false);
        }
    };

    useEffect(() => {
        didMount();
    }, []);

    useEffect(() => {
        if (categories.length === 0) return;

        const currentCategory = categories.find(c => c.id === currentCategoryId);
        if (currentCategoryId > 0 && currentCategory) {
            const isCategoryAvailable = !currentCategory.places || currentCategory.places.length === 0 ||
                currentCategory.places.some(place => place.newProductPlaceId === currentPlaceId);

            if (!isCategoryAvailable) {
                const availableCategory = categories.find(category => {
                    if (!category.places || category.places.length === 0) return true;
                    return category.places.some(place => place.newProductPlaceId === currentPlaceId);
                });

                if (availableCategory) {
                    setCurrentCategoryId(availableCategory.id);
                } else if (products.findIndex(a => a.categoryId == null) !== -1) {
                    setCurrentCategoryId(-2);
                } else {
                    setCurrentCategoryId(categories.length > 0 ? categories[0].id : 1);
                }
            }
        }
    }, [currentPlaceId, categories, products]);

    const moveCategoryUp = async (categoryId) => {
        const sortedCategories = [...availableCategories].sort((a, b) => a.priority - b.priority);
        const currentIndex = sortedCategories.findIndex(c => c.id === categoryId);

        if (currentIndex <= 0) return;

        const tempPriority = sortedCategories[currentIndex].priority;
        sortedCategories[currentIndex].priority = sortedCategories[currentIndex - 1].priority;
        sortedCategories[currentIndex - 1].priority = tempPriority;

        try {
            await reorderCategories([
                { id: sortedCategories[currentIndex].id, priority: sortedCategories[currentIndex].priority },
                { id: sortedCategories[currentIndex - 1].id, priority: sortedCategories[currentIndex - 1].priority }
            ], jwt);

            const updatedCategories = categories.map(cat => {
                const updatedCat = sortedCategories.find(c => c.id === cat.id);
                return updatedCat ? { ...cat, priority: updatedCat.priority } : cat;
            });

            setCategories(updatedCategories);
        } catch (error) {
            console.error("Ошибка обновления порядка категорий:", error);
            alert("Не удалось обновить порядок");
        }
    };

    const moveCategoryDown = async (categoryId) => {
        const sortedCategories = [...availableCategories].sort((a, b) => a.priority - b.priority);
        const currentIndex = sortedCategories.findIndex(c => c.id === categoryId);

        if (currentIndex >= sortedCategories.length - 1) return;

        const tempPriority = sortedCategories[currentIndex].priority;
        sortedCategories[currentIndex].priority = sortedCategories[currentIndex + 1].priority;
        sortedCategories[currentIndex + 1].priority = tempPriority;

        try {
            await reorderCategories([
                { id: sortedCategories[currentIndex].id, priority: sortedCategories[currentIndex].priority },
                { id: sortedCategories[currentIndex + 1].id, priority: sortedCategories[currentIndex + 1].priority }
            ], jwt);

            const updatedCategories = categories.map(cat => {
                const updatedCat = sortedCategories.find(c => c.id === cat.id);
                return updatedCat ? { ...cat, priority: updatedCat.priority } : cat;
            });

            setCategories(updatedCategories);
        } catch (error) {
            console.error("Ошибка обновления порядка категорий:", error);
            alert("Не удалось обновить порядок");
        }
    };

    const moveProductUp = async (productId) => {
        const currentProducts = products.filter(p => {
            const categoryMatch = p.categoryId === (currentCategoryId <= 0 ? null : currentCategoryId);
            const placeMatch = !p.places || p.places.length === 0 ||
                p.places.some(place => place.productPlaceId === currentPlaceId);
            return categoryMatch && placeMatch;
        });

        const sortedProducts = [...currentProducts].sort((a, b) => a.priority - b.priority);
        const currentIndex = sortedProducts.findIndex(p => p.id === productId);

        if (currentIndex <= 0) return;

        const updateRequests = [
            { id: sortedProducts[currentIndex].id, priority: sortedProducts[currentIndex - 1].priority },
            { id: sortedProducts[currentIndex - 1].id, priority: sortedProducts[currentIndex].priority }
        ];

        try {
            const result = await reorderProducts(updateRequests, jwt);

            if (result) {
                const updatedProducts = products.map(prod => {
                    if (prod.id === sortedProducts[currentIndex].id) {
                        return { ...prod, priority: sortedProducts[currentIndex - 1].priority };
                    }
                    if (prod.id === sortedProducts[currentIndex - 1].id) {
                        return { ...prod, priority: sortedProducts[currentIndex].priority };
                    }
                    return prod;
                });

                setProducts(updatedProducts);
            }
        } catch (error) {
            console.error("Ошибка обновления порядка продуктов:", error);
            alert("Не удалось обновить порядок");
        }
    };

    const moveProductDown = async (productId) => {
        const currentProducts = products.filter(p => {
            const categoryMatch = p.categoryId === (currentCategoryId <= 0 ? null : currentCategoryId);
            const placeMatch = !p.places || p.places.length === 0 ||
                p.places.some(place => place.productPlaceId === currentPlaceId);
            return categoryMatch && placeMatch;
        });

        const sortedProducts = [...currentProducts].sort((a, b) => a.priority - b.priority);
        const currentIndex = sortedProducts.findIndex(p => p.id === productId);

        if (currentIndex >= sortedProducts.length - 1) return;

        const updateRequests = [
            { id: sortedProducts[currentIndex].id, priority: sortedProducts[currentIndex + 1].priority },
            { id: sortedProducts[currentIndex + 1].id, priority: sortedProducts[currentIndex].priority }
        ];

        try {
            const result = await reorderProducts(updateRequests, jwt);

            if (result) {
                const updatedProducts = products.map(prod => {
                    if (prod.id === sortedProducts[currentIndex].id) {
                        return { ...prod, priority: sortedProducts[currentIndex + 1].priority };
                    }
                    if (prod.id === sortedProducts[currentIndex + 1].id) {
                        return { ...prod, priority: sortedProducts[currentIndex].priority };
                    }
                    return prod;
                });

                setProducts(updatedProducts);
            }
        } catch (error) {
            console.error("Ошибка обновления порядка продуктов:", error);
            alert("Не удалось обновить порядок");
        }
    };

    const ProductItem = ({ product, index, totalItems }) => {
        const [isEditProduct, setIsEditProduct] = useState(false);

        return (
            <ListGroup.Item variant="success">
                <Stack
                    gap={3}
                    direction={window.innerWidth <= 550 ? 'vertical' : 'horizontal'}
                    style={{
                        display: 'flex',
                        justifyContent: window.innerWidth <= 550 ? 'center' : 'space-between',
                        alignItems: window.innerWidth <= 550 ? 'stretch' : 'center'
                    }}>
                    <Stack gap={1} direction="horizontal">
                        {isEditingProductOrder && (
                            <Stack gap={1} direction="horizontal">
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        moveProductUp(product.id);
                                    }}
                                    disabled={index === 0}
                                >
                                    <FaArrowUp />
                                </Button>
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        moveProductDown(product.id);
                                    }}
                                    disabled={index === totalItems - 1}
                                >
                                    <FaArrowDown />
                                </Button>
                            </Stack>
                        )}

                        <span style={{ textAlign: 'center' }}>{product.name}</span>
                        <FaCircleInfo
                            onClick={() => setIsEditProduct(true)}
                            style={{ cursor: 'pointer' }}
                        />
                    </Stack>
                </Stack>

                {isEditProduct && (
                    <EditNewProduct
                        categories={categories}
                        places={places}
                        jwt={jwt}
                        onDeleted={() => setProducts(products.filter(f => f.id != product.id))}
                        onHide={() => setIsEditProduct(false)}
                        onUpdate={(u) => {
                            const temp = [...products];
                            const index = temp.findIndex(f => f.id == product.id);
                            temp[index] = u;
                            setProducts(temp);
                            setIsEditProduct(false);
                        }}
                        product={product}
                        show={isEditProduct}
                    />
                )}
            </ListGroup.Item>
        );
    };

    const availableCategories = categories.filter(category => {
        if (!category.places || category.places.length === 0) return true;
        return category.places.some(place => place.newProductPlaceId === currentPlaceId);
    });

    const tempCategoryId = currentCategoryId <= 0 ? null : currentCategoryId;
    const productsOnCurrentCategory = products.filter(p => {
        const categoryMatch = p.categoryId === tempCategoryId;
        const placeMatch = !p.places || p.places.length === 0 ||
            p.places.some(place => place.productPlaceId === currentPlaceId);
        return categoryMatch && placeMatch;
    });

    const sortedAvailableCategories = [...availableCategories].sort((a, b) => a.priority - b.priority);
    const sortedProductsOnCurrentCategory = [...productsOnCurrentCategory].sort((a, b) => a.priority - b.priority);

    if (loading || fixingPriorities) {
        return (
            <div style={{ padding: '40px 8px', margin: '0px auto', width: '100%', maxWidth: '1440px' }}>
                <div className="text-center">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Загрузка...</span>
                    </div>
                    <p className="mt-2">
                        {fixingPriorities ? 'Автоматическое исправление приоритетов...' : 'Загрузка данных...'}
                    </p>
                    {fixingPriorities && (
                        <Alert variant="info" className="mt-3">
                            <p>Обнаружены дубликаты приоритетов. Исправляем автоматически...</p>
                            <p className="small text-muted mb-0">Это может занять несколько секунд</p>
                        </Alert>
                    )}
                </div>
            </div>
        );
    }

    if (!hasAccess) {
        return (
            <div style={{ padding: '40px 8px', margin: '0px auto', width: '100%', maxWidth: '1440px' }}>
                <Alert variant="danger">
                    <Alert.Heading>Нет доступа к отправкам</Alert.Heading>
                    <p>
                        У вас нет прав для доступа к системе отправок.
                        Если вы считаете, что это ошибка, обратитесь к администратору.
                    </p>
                </Alert>
            </div>
        );
    }

    return (
        <div>
            <div style={{ padding: '40px 8px', margin: '0px auto', width: '100%', maxWidth: '1440px' }}>
                <Stack gap={3}>
                    <Form>
                        <InputGroup>
                            <InputGroup.Text>Дата</InputGroup.Text>
                            <Form.Control
                                value={date}
                                onChange={(e) => setDate(e.target.value)}
                                type="date"
                                style={{ maxWidth: '240px' }}
                            />
                        </InputGroup>
                    </Form>

                    <InputGroup>
                        <InputGroup.Text>Платформа</InputGroup.Text>
                        <ToggleButtonGroup type="radio" id="place-select" style={{ overflowX: 'auto' }} name="options" value={currentPlaceId} onChange={(e) => e == -1 ? null : setCurrentPlaceId(e)}>
                            {places && places.map(p => (
                                <ToggleButton
                                    key={p.id}
                                    onClick={() => setPlacePoints({ id: p.id, count: placePoints.id == p.id ? placePoints.count + 1 : 1 })}
                                    id={"tbg-radio-place-" + p.id}
                                    style={{ display: 'flex', alignItems: 'center' }}
                                    variant={currentPlaceId == p.id ? 'primary' : 'light'}
                                    value={p.id}>
                                    <span>{p.name}</span>
                                </ToggleButton>
                            ))}
                            <ToggleButton id={"tbg-radio-place-create"} onClick={() => setIsCreatePlace(true)} style={{ display: 'flex', alignItems: 'center' }} variant={'success'} value={-1}>
                                <FaPlus />
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </InputGroup>

                    <InputGroup>
                        <Stack direction="horizontal" gap={2} style={{ width: '100%' }}>
                            <InputGroup.Text>Категория</InputGroup.Text>
                            <Button
                                variant={isEditingCategoryOrder ? "warning" : "outline-secondary"}
                                size="sm"
                                onClick={() => setIsEditingCategoryOrder(!isEditingCategoryOrder)}
                                style={{ marginLeft: 'auto', marginRight: '10px' }}
                            >
                                {isEditingCategoryOrder ? "Закончить" : "Изм. порядок"}
                            </Button>
                        </Stack>

                        <ToggleButtonGroup
                            type="radio"
                            id="category-select"
                            style={{ overflowX: 'auto', marginTop: '10px' }}
                            name="options"
                            value={currentCategoryId}
                            onChange={(e) => e == -1 ? null : setCurrentCategoryId(e)}
                        >
                            {sortedAvailableCategories.map((category, index) => (
                                <ToggleButton
                                    key={category.id}
                                    onClick={() => {
                                        if (isEditingCategoryOrder) {
                                            return;
                                        }
                                        setCategoryPoints({ id: category.id, count: categoryPoints.id == category.id ? categoryPoints.count + 1 : 1 });
                                    }}
                                    id={"tbg-radio-category-" + category.id}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        marginRight: '5px'
                                    }}
                                    variant={currentCategoryId == category.id ? 'primary' : 'light'}
                                    value={category.id}
                                >
                                    <Stack direction="horizontal" gap={1}>
                                        {isEditingCategoryOrder && (
                                            <Stack direction="horizontal" gap={1}>
                                                <Button
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        moveCategoryUp(category.id);
                                                    }}
                                                    disabled={index === 0}
                                                    style={{ padding: '2px 5px' }}
                                                >
                                                    <FaArrowLeft />
                                                </Button>
                                                <Button
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        moveCategoryDown(category.id);
                                                    }}
                                                    disabled={index === sortedAvailableCategories.length - 1}
                                                    style={{ padding: '2px 5px' }}
                                                >
                                                    <FaArrowRight />
                                                </Button>
                                            </Stack>
                                        )}

                                        <span>{category.name}</span>
                                    </Stack>
                                </ToggleButton>
                            ))}

                            {products.filter(p => p.categoryId == null).some(product =>
                                !product.places || product.places.length === 0 ||
                                product.places.some(place => place.productPlaceId === currentPlaceId)
                            ) && (
                                    <ToggleButton
                                        id={"tbg-radio-category-no"}
                                        style={{ display: 'flex', alignItems: 'center' }}
                                        variant={currentCategoryId == -2 ? 'primary' : 'light'}
                                        value={-2}
                                    >
                                        <span>Без категории</span>
                                    </ToggleButton>
                                )}

                            <ToggleButton
                                id={"tbg-radio-place-create"}
                                style={{ display: 'flex', alignItems: 'center' }}
                                variant={'success'}
                                onClick={() => setIsCreateCategory(true)}
                                value={-1}>
                                <FaPlus />
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </InputGroup>

                    <ListGroup>
                        <Stack direction="horizontal" style={{ marginBottom: '10px' }}>
                            <Button
                                variant={isEditingProductOrder ? "warning" : "outline-secondary"}
                                size="sm"
                                onClick={() => setIsEditingProductOrder(!isEditingProductOrder)}
                                style={{ marginLeft: 'auto' }}
                            >
                                {isEditingProductOrder ? "Закончить" : "Изм. порядок продуктов"}
                            </Button>
                        </Stack>

                        {sortedProductsOnCurrentCategory.length > 0 ? (
                            sortedProductsOnCurrentCategory.map((product, index) => (
                                <ProductItem
                                    key={product.id}
                                    product={product}
                                    index={index}
                                    totalItems={sortedProductsOnCurrentCategory.length}
                                />
                            ))
                        ) : (
                            <Alert variant="warning">Пусто</Alert>
                        )}
                        <ListGroup.Item
                            action
                            onClick={() => setIsCreateProduct(true)}
                            variant="success"
                            style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <span>Создать</span>
                        </ListGroup.Item>
                    </ListGroup>
                </Stack>

                {isCreatePlace && (
                    <CreatePlace
                        show={isCreatePlace}
                        onHide={() => setIsCreatePlace(false)}
                        jwt={jwt}
                        onCreated={(e) => setPlaces([...places, e]) || setIsCreatePlace(false)}
                    />
                )}

                {isCreateCategory && (
                    <CreateProductCategory
                        show={isCreateCategory}
                        onHide={() => setIsCreateCategory(false)}
                        jwt={jwt}
                        onCreated={(e) => {
                            setCategories([...categories, e]);
                            setIsCreateCategory(false);
                        }}
                        priority={categories.length > 0 ? Math.max(...categories.map(c => c.priority)) + 1 : 0}
                        places={places}
                    />
                )}

                {placePoints.count >= 3 && (
                    <EditPlace
                        show={placePoints.count >= 3}
                        onHide={() => setPlacePoints({})}
                        jwt={jwt}
                        place={places.find(p => p.id == placePoints.id)}
                        onDeleted={() => {
                            const newPlaces = [...places.filter(f => f.id != placePoints.id)];
                            setPlaces(newPlaces);
                            setPlacePoints({});
                            setCurrentPlaceId(newPlaces.length > 0 ? newPlaces[0].id : -1);
                        }}
                        onUpdate={(u) => {
                            const temp = [...places];
                            const index = temp.findIndex(f => f.id == u.id);
                            temp[index] = u;
                            setPlaces(temp);
                            setPlacePoints({});
                        }}
                        places={places}
                    />
                )}

                {categoryPoints.count >= 3 && (
                    <EditCategory
                        show={categoryPoints.count >= 3}
                        onHide={() => setCategoryPoints({})}
                        jwt={jwt}
                        category={categories.find(p => p.id == categoryPoints.id)}
                        onDeleted={(properties) => {
                            if (properties.statusProducts == 'move') {
                                setProducts([...products.map(p => {
                                    return p.categoryId == categoryPoints.id
                                        ? { ...p, categoryId: properties.newCategory }
                                        : p;
                                })]);
                            } else {
                                setProducts([...products.filter(p => p.categoryId != categoryPoints.id)]);
                            }

                            const newCategories = [...categories.filter(f => f.id != categoryPoints.id)];
                            setCategories(newCategories);

                            if (currentCategoryId === categoryPoints.id) {
                                const availableCategory = newCategories.find(category => {
                                    if (!category.places || category.places.length === 0) return true;
                                    return category.places.some(place => place.newProductPlaceId === currentPlaceId);
                                });
                                setCurrentCategoryId(availableCategory ? availableCategory.id : -2);
                            }

                            setCategoryPoints({});
                        }}
                        onUpdate={(u) => {
                            const temp = [...categories];
                            const index = temp.findIndex(f => f.id == u.id);
                            temp[index] = u;
                            setCategories(temp);
                            setCategoryPoints({});
                        }}
                        categories={categories}
                        places={places}
                    />
                )}

                {isCreateProduct && (
                    <CreateNewProduct
                        categories={categories}
                        places={places}
                        jwt={jwt}
                        onCreated={(c) => {
                            setProducts([...products, c]);
                            setIsCreateProduct(false);
                        }}
                        onHide={() => setIsCreateProduct(false)}
                        show={isCreateProduct}
                        categoryId={currentCategoryId > 0 ? currentCategoryId : null}
                        products={products}
                    />
                )}
                <button
                    className="btn btn-outline-primary me-2"
                    onClick={handleExport}
                    disabled={exporting || products.length === 0}
                >
                    {exporting ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Экспорт...
                        </>
                    ) : (
                        <>
                            <i className="bi bi-file-earmark-excel me-1"></i> Экспорт продуктов в excel
                        </>
                    )}
                </button>
            </div>
        </div>
    );
}

export default SellNewProducts;